<script setup lang="ts">
interface ExperienceItem {
  company: string
  role: string
  period: string
  current: boolean
  description: string
  techStack: string[]
  highlights?: string[]
}

const { data: experienceDoc } = await useAsyncData('experience', () =>
  queryCollection('experience').first()
)

const experiences = computed(() => ((experienceDoc.value as { items?: ExperienceItem[] } | null)?.items ?? []) as ExperienceItem[])

const showAll = ref(false)
const expandedIndices = ref(new Set<number>())

const visibleExperiences = computed(() => showAll.value ? experiences.value : experiences.value.slice(0, 3))
const hasMore = computed(() => experiences.value.length > 3)

function isExpanded(idx: number) {
  return expandedIndices.value.has(idx)
}

function onToggle(idx: number) {
  const next = new Set(expandedIndices.value)
  if (next.has(idx)) {
    next.delete(idx)
  } else {
    next.add(idx)
  }
  expandedIndices.value = next
}

async function toggleShowAll() {
  const wasShowAll = showAll.value
  showAll.value = !showAll.value
  if (wasShowAll && import.meta.client) {
    expandedIndices.value = new Set()
    await nextTick()
    document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
</script>

<template>
  <section
    id="experience"
    class="py-12"
  >
    <h2 class="text-xs font-semibold uppercase tracking-widest text-muted mb-6">
      Work Experience
    </h2>

    <div class="relative">
      <!-- Vertical timeline line -->
      <div class="absolute left-[5px] top-2 bottom-4 w-px bg-border" />

      <ExperienceTimelineItem
        v-for="(exp, index) in visibleExperiences"
        :key="index"
        :company="exp.company"
        :role="exp.role"
        :period="exp.period"
        :current="exp.current"
        :description="exp.description"
        :tech-stack="exp.techStack"
        :highlights="exp.highlights"
        :expanded="isExpanded(index)"
        @toggle="onToggle(index)"
      />
    </div>

    <div
      v-if="hasMore"
      class="flex justify-center mt-2"
    >
      <UButton
        variant="ghost"
        color="neutral"
        :trailing-icon="showAll ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click="toggleShowAll"
      >
        {{ showAll ? 'Show less' : `Show ${experiences.length - 3} more experiences` }}
      </UButton>
    </div>
  </section>
</template>
